/* eslint-disable no-unused-vars */
"use client"

import styled from "@emotion/styled"
import { Button } from "@mui/material"
import { Dispatch, SetStateAction } from "react"
import { CircleXIcon, NewText, Phone4RentIcon } from "ui"
import { contactButtonClick } from "utils"
import { RentProjectContactText } from "utils/projects"
import { ProjectsMessages } from "./Projects"

const StyledAbsolute = styled.div`
  width: fit-content;
  display: flex;
  flex-direction: column;
  align-items: center;
`
const HiddenButtons = styled.div`
  position: absolute;
  bottom: 64px;
  left: 50%;
  transform: translateX(-50%);
  &.newStyle {
    top: 60px;
    bottom: unset;
    display: flex;
    flex-direction: column-reverse;

    ${(props) => props.theme.breakpoints.up("lg")} {
      top: unset;
      display: block;
      bottom: 64px;
    }
  }
`
const StickyOutside = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  background-color: white;
  border-radius: 200px;
  height: 74px;
  width: 180px;
  box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.16);
  &.newStyle {
    box-shadow: none;
    border-radius: 8px;
    border: 2px solid white;
    background-color: ${(props) => props.theme.colors.blue4rentMarketing};
    width: 120px;
    font-size: 16px;
    font-weight: 400;
    line-height: 145%;
    height: 40px;
    ${(props) => props.theme.breakpoints.up("lg")} {
      width: 180px;
      font-size: 20px;
      font-weight: 700;
      line-height: 24px;
      width: 180px;
      height: 48px;
    }
  }
  :hover {
    border: 1px solid black;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    width: 196px;
  }
`
const RelativeDiv = styled.div`
  position: relative;
`
const StyledButton = styled(Button)`
  background-color: white;
  margin-bottom: 16px;
  min-height: 53px;
  width: 164px;
  padding-left: 0;
  padding-right: 0;
  color: ${(props) => props.theme.colors.darkBlue4rentMarketing};
  ${(props) => props.theme.breakpoints.up("md")} {
    width: 180px;
  }

  &.newStyle {
    width: unset;
    border-radius: 8px;
    min-height: max-content;
    height: 40px;
    padding: 0 3px;
    width: 120px;
    font-size: 16px;
    margin-bottom: 8px;

    border-width: 1px !important;
    ${(props) => props.theme.breakpoints.up("lg")} {
      padding: 0 16px;
      margin-bottom: 0;
      margin-top: 8px;
      width: 180px;
      font-size: 18px;
    }
  }
`

const StyledStickButton = styled(Button)<{ hide?: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 58px;
  color: white;
  white-space: nowrap;
  background-color: ${(props) => props.theme.colors.propBlue};
  border: none;

  &.newStyle {
    background-color: ${(props) => props.theme.colors.blue4rentMarketing};

    padding: 3px 16px;
    height: unset;
    .phone-icon {
      display: none;
    }
    ${(props) => props.theme.breakpoints.up("lg")} {
      flex-direction: row;
      gap: 4px;
      padding: 3px 24px;
      .phone-icon {
        display: unset;
        min-width: 25px;
        height: 25px;
      }
    }

    ${(props) => (props.hide ? "display: none !important;" : "")}
  }
  :hover,
  :active {
    border: none;
    box-shadow: none;
  }
`

const StyledCloseText = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const StyledPhoneLink = styled.a`
  ${(props) => props.theme.breakpoints.up("lg")} {
    pointer-events: none;
  }
`

type Props = {
  messages: ProjectsMessages
  setIsBackdropVisible: Dispatch<SetStateAction<boolean>>
  open: boolean
  setIsDropDownOpen: Dispatch<SetStateAction<boolean>>
  setIsFormVisible: Dispatch<SetStateAction<boolean>>
  phone?: string
  phoneName?: string
  miniSite?: string
  virtualTourLink?: string
  projectType?: string
  virtualTourLinkName?: string
  isForMarketingRentPage?: boolean
  locale?: string
  bmbyMeetingId?: string | number
  rentProjectContactText?: RentProjectContactText
  hideLeadForm?: boolean
}

function StickyCTA({
  setIsBackdropVisible,
  setIsFormVisible,
  setIsDropDownOpen,
  open,
  miniSite,
  phone,
  virtualTourLink,
  messages,
  projectType,
  virtualTourLinkName,
  isForMarketingRentPage,
  locale,
  bmbyMeetingId = "5BAE7E64-8643-4844-BB7B-C87B6FDC2A9A",
  rentProjectContactText,
  hideLeadForm,
}: Props) {
  const isLegacy = projectType === "מורשת"
  const isPlanning = projectType === "תכנון ובנייה"

  const handleClick = () => {
    contactButtonClick(
      "יצירת קשר לקבלת מחיר מנציג/ה",
      !open ? "open" : "close",
      "כפתור פופ אפ יצירת קשר",
    )
    setIsBackdropVisible((prev) => !prev)
    setIsDropDownOpen((prev) => !prev)
  }
  const handleFormClick = () => {
    if (isForMarketingRentPage) {
      setIsBackdropVisible(false)
    }
    setIsDropDownOpen(false)
    setIsFormVisible(true)
    contactButtonClick("יצירת קשר לקבלת מחיר מנציג/ה", "השארת פרטים", "כפתור פופ אפ יצירת קשר")
  }

  const leadButton = rentProjectContactText?.leadButton
  const contactButton = rentProjectContactText?.contactButton
  const meetingButton = rentProjectContactText?.meetingButton
  const callButton = rentProjectContactText?.callButton?.[0]
  const phoneNumber = isForMarketingRentPage
    ? callButton
      ? {
          href: callButton?.url,
          name: callButton?.name || messages.callNow,
        }
      : null
    : {
        href: phone,
        name: messages.salesCenter || messages.callNow,
      }

  const handleScheduleBmbyMeeting = () => {
    const lang = locale === "en" ? "en" : "he"
    const url = `https://bmbyscheduler.azurewebsites.net/?meeting=${bmbyMeetingId}&lang=${lang}`
    const popup = window.open(url, "bmbyScheduler", "width=940,height=500")
    if (!popup) return // in case popup was blocked

    const timer = setInterval(() => {
      if (popup.closed) {
        clearInterval(timer) // ✅ cleanup
        // success logic here
      }
    }, 500)

    // ✅ extra safety: cleanup if user leaves page
    window.addEventListener("beforeunload", () => clearInterval(timer))
  }

  if (isLegacy) {
    return null
  }
  if (isForMarketingRentPage && !contactButton) {
    return null
  }
  return (
    <>
      <StyledAbsolute className={isForMarketingRentPage ? "newStyle" : ""}>
        <RelativeDiv>
          {open ? (
            <>
              <HiddenButtons className={isForMarketingRentPage ? "newStyle" : ""}>
                {!hideLeadForm && (
                  <StyledButton
                    variant="outlined"
                    size="large"
                    onClick={handleFormClick}
                    className={isForMarketingRentPage ? "newStyle" : ""}
                  >
                    {isForMarketingRentPage ? leadButton : messages.leaveDetails}
                  </StyledButton>
                )}
                {isPlanning ? null : (
                  <>
                    {isForMarketingRentPage ? (
                      meetingButton && bmbyMeetingId ? (
                        <StyledButton
                          className={isForMarketingRentPage ? "newStyle" : ""}
                          variant="outlined"
                          size="large"
                          onClick={handleScheduleBmbyMeeting}
                        >
                          {meetingButton}
                        </StyledButton>
                      ) : null
                    ) : (
                      <>
                        {/* {miniSite ? ( NOTE: keeping it just commented out in case we will need to uncomment in future
                          <Link href={miniSite}>
                            <StyledButton
                              className={isForMarketingRentPage ? "newStyle" : ""}
                              variant="outlined"
                              size="large"
                              onClick={() =>
                                contactButtonClick(
                                  "יצירת קשר לקבלת מחיר מנציג/ה",
                                  "לאתר מכירות",
                                  "כפתור פופ אפ יצירת קשר",
                                )
                              }
                            >
                              {messages.sales}
                            </StyledButton>
                          </Link>
                        ) : null}
                        {virtualTourLink ? (
                          <Link href={virtualTourLink}>
                            <StyledButton
                              className={isForMarketingRentPage ? "newStyle" : ""}
                              variant="outlined"
                              size="large"
                              onClick={() =>
                                contactButtonClick(
                                  "יצירת קשר לקבלת מחיר מנציג/ה",
                                  "לסיור וירטואלי",
                                  "כפתור פופ אפ יצירת קשר",
                                )
                              }
                            >
                              {virtualTourLinkName}
                            </StyledButton>
                          </Link>
                        ) : null} */}
                      </>
                    )}
                    {!isForMarketingRentPage
                      ? null
                      : phoneNumber && (
                          <StyledPhoneLink
                            href={phoneNumber.href}
                            onClick={() =>
                              contactButtonClick(
                                "יצירת קשר לקבלת מחיר מנציג/ה",
                                "שיחה עם מוקד מכירות",
                                "כפתור פופ אפ יצירת קשר",
                              )
                            }
                          >
                            <StyledButton
                              className={isForMarketingRentPage ? "newStyle" : ""}
                              variant="outlined"
                              size="large"
                              role="presentation"
                              tabIndex={-1}
                            >
                              {phoneNumber.name}
                            </StyledButton>
                          </StyledPhoneLink>
                        )}
                  </>
                )}
              </HiddenButtons>
            </>
          ) : null}
          <StickyOutside className={isForMarketingRentPage ? "newStyle" : ""}>
            <StyledStickButton
              className={isForMarketingRentPage ? "newStyle" : ""}
              variant="outlined"
              onClick={handleClick}
            >
              {open ? (
                <StyledCloseText>
                  <CircleXIcon />
                  <NewText
                    component={"h3"}
                    desktopFontSize={18}
                    mobileFontSize={16}
                    inheritColor={true}
                    desktopFontWeight={600}
                  >
                    {messages.close}
                  </NewText>
                </StyledCloseText>
              ) : (
                <>
                  {isForMarketingRentPage && (
                    <Phone4RentIcon color="white" className="phone-icon" />
                  )}
                  <NewText
                    component={"h4"}
                    desktopFontSize={isForMarketingRentPage ? 20 : 18}
                    desktopFontWeight={isForMarketingRentPage ? 700 : 600}
                    mobileFontSize={16}
                    inheritColor={true}
                  >
                    {isForMarketingRentPage ? contactButton : messages.contactUs}
                  </NewText>
                </>
              )}
            </StyledStickButton>
          </StickyOutside>
        </RelativeDiv>
      </StyledAbsolute>
    </>
  )
}

export default StickyCTA
